import { useDebounce } from "@/app/hooks/useDebounce";
import { cltService } from "@/app/services/cltService";
import { ITableClt } from "@/app/services/cltService/getTables";
import { customerService } from "@/app/services/customerService";
import { transactionsService } from "@/app/services/transactionService";
import { formatCpfNumber } from "@/app/utils/cpfFormater";
import { formatDate } from "@/app/utils/formatDate";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";
import { formatZipCode } from "@/app/utils/formatZipCode";
import masks from "@/app/utils/masks";
import { states } from "@/app/utils/states";
import { useToast } from "@/view/components/ui/use-toast";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { useRegisterSupportContext } from "@/app/store/supportScreenContext";
import { PartnerType } from "@/app/utils/partners.enum";

export type CltSimulationMode = "liberado" | "parcela";

const SLIDER_MIN = 0;

export default function useAvailableOffersClt() {
  const { cpf } = useParams();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const matriculaParam = searchParams.get("matricula") ?? undefined;
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [simulationMode, setSimulationMode] =
    useState<CltSimulationMode>("liberado");
  const [financedValue, setFinancedValue] = useState<number | undefined>(
    undefined,
  );
  const [installmentValue, setInstallmentValue] = useState<number | undefined>(
    undefined,
  );
  const [maxInstallment, setMaxInstallment] = useState<number | undefined>(
    undefined,
  );
  const [maxWithdraw, setMaxWithdraw] = useState<number | undefined>(undefined);
  const [prazoParam, setPrazoParam] = useState<number | undefined>(undefined);

  const debouncedValue = useDebounce(1500, financedValue?.toString());
  const debouncedInstallment = useDebounce(1500, installmentValue?.toString());

  const valorFinanciado =
    simulationMode === "liberado" && debouncedValue
      ? Number(debouncedValue)
      : undefined;

  const valorParcela =
    simulationMode === "parcela" && debouncedInstallment
      ? Number(debouncedInstallment)
      : undefined;

  const exceedsMaxInstallment =
    !!valorParcela && !!maxInstallment && valorParcela > maxInstallment;

  const {
    data: availableOffers,
    isLoading: isLoadingFirstSimulation,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "availableOffers",
      cpf,
      valorFinanciado,
      valorParcela,
      matriculaParam,
      prazoParam,
    ],
    queryFn: () =>
      cltService.getTables({
        cpf: masks.cpf(cpf),
        valorFinanciado,
        valorParcela,
        matricula: matriculaParam,
        quantidadeParcelas: prazoParam,
      }),
    enabled:
      !!cpf &&
      debouncedValue !== "0" &&
      debouncedInstallment !== "0" &&
      !exceedsMaxInstallment,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (availableOffers?.valorParcelaMaximo) {
      setMaxInstallment(availableOffers.valorParcelaMaximo);
    }
  }, [availableOffers]);

  useEffect(() => {
    if (valorFinanciado || valorParcela || prazoParam) return;

    const tables = availableOffers?.tables ?? [];

    if (tables.length === 0) return;

    setMaxWithdraw(
      Math.max(...tables.map((table) => table.ValorLiquidoCliente || 0)),
    );
  }, [availableOffers, valorFinanciado, valorParcela, prazoParam]);

  useEffect(() => {
    if (!exceedsMaxInstallment) return;

    toast({
      variant: "destructive",
      title: "Valor da parcela acima do permitido",
      description: `O valor máximo permitido é de ${addMoneySimbols(maxInstallment)}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exceedsMaxInstallment, valorParcela]);

  const hasCustomValue = !!financedValue || !!installmentValue;

  const isLoading = isLoadingFirstSimulation || isFetching;

  const isLoadingAvailableOffers = isLoadingFirstSimulation && !hasCustomValue;
  const isErrorAvailableOffers = !!error && !hasCustomValue;
  const isErrorFinancedValue = !!error && hasCustomValue;

  const { data: customer, isLoading: customerIsLoading } = useQuery({
    queryKey: ["getCustomer", cpf],
    queryFn: () => (cpf ? customerService.customerInfo(cpf) : undefined),
  });

  const [selectedTab, setSelectedTab] = useState(0);
  const [skip, setSkip] = useState(0);

  const tabs = {
    tables: selectedTab === 0,
    transaction: selectedTab === 1,
    personalInfo: selectedTab === 2,
  };

  const customerId = customer?._id;

  const transactions = useQuery({
    queryKey: ["transactions", "allTransactions", skip, customerId],
    queryFn: () =>
      customerId
        ? transactionsService.transaction({ skip, customerIds: customerId })
        : undefined,
  });

  const form = useForm();

  const { reset } = form;

  useEffect(() => {
    if (!customer) return;

    reset({
      ...customer,
      birthDate: customer.birthDate ? formatDate(customer.birthDate) : "",
      cpf: customer.cpf ? formatCpfNumber(customer.cpf.toString()) : "",
      address: {
        ...customer.address,
        zipCode: customer.address?.zipCode
          ? formatZipCode(customer.address.zipCode)
          : "",
        state:
          states.find(({ value }) => value === customer.address?.state)
            ?.label ?? "",
      },
      bankAccount: {
        ...customer.bankAccount,
      },
    });
  }, [customer, reset]);

  useEffect(() => {
    if (availableOffers && availableOffers.tables?.length > 0) {
      setSelectedIndex(0);
    }
  }, [availableOffers]);

  const list = useMemo(
    () => (availableOffers?.tables ?? []) as ITableClt[],
    [availableOffers],
  );

  const selectedTable =
    selectedIndex !== null ? (list[selectedIndex] ?? null) : null;

  const prazos = useMemo(
    () => availableOffers?.prazos ?? [],
    [availableOffers],
  );

  const selectedPrazo =
    prazoParam ?? availableOffers?.prazoSelecionado ?? list[0]?.Prazo ?? null;

  const isWithdrawMode = simulationMode === "liberado";

  const currentValue = isWithdrawMode
    ? (financedValue ?? selectedTable?.ValorLiquidoCliente ?? 0)
    : (installmentValue ?? selectedTable?.ValorParcela ?? 0);

  const setCurrentValue = isWithdrawMode
    ? setFinancedValue
    : setInstallmentValue;

  const valueLabel = isWithdrawMode
    ? "Valor que deseja sacar"
    : "Valor da parcela";

  const sliderMax = (isWithdrawMode ? maxWithdraw : maxInstallment) ?? 0;
  const hasSlider = sliderMax > SLIDER_MIN;
  const sliderValue = Math.min(Math.max(currentValue, SLIDER_MIN), sliderMax);

  const visibleOffers = useMemo(
    () => list.map((offer, index) => ({ offer, index })),
    [list],
  );

  const handleRefresh = () => {
    setFinancedValue(undefined);
    setInstallmentValue(undefined);
    setPrazoParam(undefined);
    setSelectedIndex(null);
    refetch();
  };

  const handleChangeSimulationMode = (mode: CltSimulationMode) => {
    if (mode === simulationMode) return;

    setSimulationMode(mode);
    setFinancedValue(undefined);
    setInstallmentValue(undefined);

    if (hasCustomValue) setPrazoParam(undefined);
  };

  const handleSelectPrazo = (prazo: number) => {
    if (prazo === selectedPrazo) return;

    setPrazoParam(prazo);
    setSelectedIndex(0);
  };

  useRegisterSupportContext("simulation.clt", {
    product: PartnerType.CLT,
    step: "Ofertas CLT",
    data: {
      cpf,
      customerName: customer?.name,
      matricula: availableOffers?.matricula ?? matriculaParam,
      simulationMode,
      installments: selectedPrazo,
      requestedValue: currentValue,
      financedValue,
      installmentValue,
    },
  });

  return {
    customer,
    customerIsLoading,
    tabs,
    setSelectedTab,
    transactions,
    setSkip,
    form,
    cpf,
    list,
    isLoading,
    error,
    selectedTable,
    selectedIndex,
    setSelectedIndex,
    prazos,
    selectedPrazo,
    handleSelectPrazo,
    visibleOffers,
    currentValue,
    setCurrentValue,
    valueLabel,
    hasSlider,
    sliderMin: SLIDER_MIN,
    sliderMax,
    sliderValue,
    sliderStep: isWithdrawMode ? 10 : 1,
    matricula: availableOffers?.matricula ?? Number(matriculaParam ?? 0),
    cnpjEmpresa: availableOffers?.cnpjEmpresa ?? 0,
    financedValue,
    setFinancedValue,
    installmentValue,
    setInstallmentValue,
    maxInstallment,
    exceedsMaxInstallment,
    simulationMode,
    handleChangeSimulationMode,
    isLoadingAvailableOffers,
    isErrorAvailableOffers,
    isErrorFinancedValue,
    handleRefresh,
    message: availableOffers?.message,
  };
}
